<script>
import partnerChoices from '@/mixins/partnerChoices';
import captialGains from '@/lib/capitalGains';
import {hasEditPermission,currentRoleLevelName, trialActiveForClients} from '@/mixins/roleLevelPermissions';
import { mapState } from 'vuex';

export default {
	mixins: [partnerChoices],
	props: {
		agreementData: Object,
		index: Number,
		propertyIndex: Number,
		updateAdditionalRealProperties: Function,
		value: Object,
		testid:String,
	},
	data() {
		return {
			isViewMode : !hasEditPermission('section-property') 
		};
	},
	computed: {
		...mapState(['currentRoleLevel']),
		additionalRealProperty() {
			return this.value;
		},
		captialGainsCalculation() {
			const {
				currentValue,
				purchasePrice,
				taxYearsOwned,
				taxYearsPrincipalResidence,
			} = this.additionalRealProperty;

			const captialGainsValue = captialGains(
				currentValue,
				purchasePrice,
				taxYearsPrincipalResidence,
				taxYearsOwned
			);
			return captialGainsValue;
		},
		partnerResponsibleChoices() {
			return this.partnerChoices.map((partner) => ({
				...partner,
				text: `${partner.text} will be solely responsible`,
			}));
		},
		partnerRentChoices() {
			return this.partnerChoices.map((partner) => ({
				...partner,
				text: `${partner.text} will keep rent`,
			}));
		},
		clientUserAgreement(){
			return trialActiveForClients() || currentRoleLevelName === 'client-sharing-user';
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
		dynamicQuestions(){
			if(this.isUserSeparatingParty){
				return {
					responsible: "Which of you is the registered holder of the debt?",
					propertyStatus: "What will you and your spouse do with the property?",
					keep: "Which one of you will keep the property?",
					upgradesBeforeSale: "Will you make any upgrades to the property before you list it for sale?",
					upgradesBeforeSale2: "How will you share the cost of the upgrades?",
					liveInUntilSold: "Who will live at the property?",
					useProperty: "Who will have use of the property until it is sold?",
					manageProperty: "How will you manage the expenses related to the property until it is sold? ",
					currentlyRented: "Is there currently rental income being generated by the property?",
					manageRentalIncome: "How will you manage rental income from the property until it is sold?",
					coOwnDecision: "We will co-own the property until one of us buys the other out or we list the property for sale.  We will decide this on:",
					purchaseAdditionalProperty: "Would either or you get the first option to purchase the other's interest in the property?",
					someoneWillLiveUntilSold: "Will either of you live in the property until it is sold?",
					personLivingInResidenceResponsibleForExpenses: "Will this person be solely responsible for the expenses?",
					manageExpenses: "How will you manage the expenses related to the property until it is sold?",
					whoCanPurchase: "Which of you will be given the first opportunity to exercise the option to purchase?"
				}
			}

			else{
				return {
					responsible: "Which party is responsible for the liability?",
					propertyStatus: "What will the parties do with the property?",
					keep: "Which party will keep the property?",
					upgradesBeforeSale: "Will the parties make any upgrades to the property before they list it for sale?",
					upgradesBeforeSale2: "How will the parties divide the cost of the upgrades?",
					liveInUntilSold: "Which Party?",
					useProperty: "Will either party have use of the property?",
					manageProperty: "How will the parties manage property expenses until it is sold?",
					currentlyRented: "Is their currently rental income being generated by the property?",
					manageRentalIncome: "How will parties manage rental income from the property until it is sold?",
					coOwnDecision: "The parties will co-own until they purchase or sell, as decided on:",
					purchaseAdditionalProperty: "Would either party like the first option to purchase the other party's interest in the property?",
					someoneWillLiveUntilSold: "Will either party live at the property until it is sold?",
					personLivingInResidenceResponsibleForExpenses: "Will this party be solely responsible for the expenses?",
					manageExpenses: "How will the parties manage the property expenses until it is sold?",
					whoCanPurchase: "Which party will be given the first opportunity to exercise the option to purchase?"
				}
			}
		},
		manageRentalIncomeOptions(){
			return[
				...this.partnerRentChoices,
				{
					text: this.isUserSeparatingParty ?  'We will share the rent equally' : 'They will share the rent equally',
					value: 'equally',
				},
				{
					text: this.isUserSeparatingParty ? 'We will share the Rent as agreed between us' :  'They will share the Rent as agreed between them',
					value: 'as-agreed',
				}
			]
		},
		manageOptions(){
			return[
				{
					text: this.isUserSeparatingParty ? 'We will share costs equally' : 'They will share costs equally',
					value: 'equally',
				},
				{
					text: this.isUserSeparatingParty ? 'We will share costs in standard way' : 'They will share costs in standard way',
					value: 'as-agreed',
				},
				{
					text: this.isUserSeparatingParty ? 'We will share costs based on an agreed-upon schedule' : 'They will share costs based on an agreed-upon schedule',
					value: 'schedule',
				},
			]
		}
	},

	watch: {
		additionalRealProperty: {
			handler: function (val) {
				this.updateAdditionalRealProperties(val, this.index);
			},
			deep: true,
		},
		value: {
			immediate: true,
			handler() {
				this.normalizeUpgradesBeforeSale();
			},
		},
	},

	methods: {
		newLiability() {
			return {
				lastFourDigits: ''
			};
		},
		parsetoString(index){
			return index.toString()
		},
		normalizeUpgradesBeforeSale() {
			const prop = this.additionalRealProperty;
			if (!prop) return;
			if (prop.upgradesBeforeSale === 'equally' || prop.upgradesBeforeSale === 'as-agreed') {
				if (!prop.upgradesCostDivision) {
					this.$set(prop, 'upgradesCostDivision', prop.upgradesBeforeSale);
				}
				prop.upgradesBeforeSale = true;
			}
		},
	},
};
</script>

<template>
	<div>
		<address-input-group
			v-model="additionalRealProperty.address"
			class="my-3"
			:testid="'additional-property-address-'+testid"
			:disabled="isViewMode"
		/>

		<radio-group
			v-model="additionalRealProperty.ownership"
			class="my-3"
			:agreement-data="agreementData"
			input-cols="4"
			label="How is the property owned?"
			reset-undefined
			partner
			select
			:options="['Jointly']"
			:testid="'additional-property-ownership-'+testid"
			:length="'long'"
			:disabled="isViewMode"
		/>

		<date-input
			v-model="additionalRealProperty.purchaseDate"
			reset-undefined
			class="my-3"
			input-cols="6"
			label="On what date was the property purchased?"
			:testid="'additional-property-purchase-date-'+testid"
			:disabled="isViewMode"
		/>

		<input-group
			v-model="additionalRealProperty.purchasePrice"
			reset-undefined
			class="my-3"
			dollar
			label="What was the purchase price of the property?"
			:testid="'additional-property-purchase-price-'+testid"
			:disabled="isViewMode"
		/>

		<input-group
			v-model="additionalRealProperty.currentValue"
			reset-undefined
			class="my-3"
			dollar
			label="What is the current value of the property?"
			:testid="'additional-property-current-value-'+testid"
			:disabled="isViewMode"
			:toolTipText="`You should use the Fair Market Value of the property. The
			assessed value is often not accurate. Under the law, the value should be as of
			the date of the Agreement unless otherwise agreed. `"
		/>
		<template v-if="!clientUserAgreement">

		<yes-no-choice
			v-model="additionalRealProperty.hadAppraisal"
			label="Was the value determined by an appraisal?"
			reset-undefined
			class="my-3"
			cols="4"
			:display-as-dropdown="true"
			:options="[
				{ value: true, text: 'Yes' },
				{
					value: false,
					text: 'No, the parties agreed to the value',
				},
			]"
			:testid="'additional-property-had-appraisal-'+testid"
			:disabled="isViewMode"
			:toolTipText="`It isn't necessary to get an appraisal if you are going to sell                              
			the property or co-own it for sometime, but it is wise to get an appraisal to
			determine the Fair Market Value of the property if either of you wishes to
			'buyout' the other person. `"
		/>

		<conditional-group
			class="my-3"
			:condition="additionalRealProperty.hadAppraisal"
		>
			<date-input
				v-model="additionalRealProperty.appraisalDate"
				label="Date of appraisal:"
				class="my-3"
				:testid="'additional-property-appraisal-date-'+testid"
				:disabled="isViewMode"
			/>
		</conditional-group>
		</template>

		<p>
			Are there any liabilities registered on title to the property? If
			so, add details of each individual liability below:
		</p>

		<list-item
			v-slot="{ item: liability, index: index }"
			v-model="additionalRealProperty.liabilities"
			:object-prototype="newLiability"
			:testid="'additional-property-new-item-'"
			:disabled="isViewMode"
		>
			<input-group
				v-model="liability.lender"
				input-cols="8"
				label="Name of Financial Institution or Lender"
				class="mb-3"
				:testid="'additional-property-'+testid+'-institution-name-'+parsetoString(index)"
				:disabled="isViewMode"
			/>

			<b-form-group
				:id="'input-registered-'+index +'-'+ propertyIndex"
				label="Liabilities registered on the property"
				:label-for="'registered-'+index +'-'+ propertyIndex"
			> 
				<b-form-select
					:id="'registered-'+index +'-'+ propertyIndex"
					v-model="liability.registered"
					size="sm"
					:options="[
						{ text: 'Mortgage', value: 'Mortgage' },
						{ text: 'Line of Credit', value: 'Line of Credit' }, // value fix
						{ text: 'Other', value: 'other' },
					]"
					:data-testid="'additional-property-'+testid+'-registered-liability-'+parsetoString(index)"
					class="col-lg-8"
					:disabled="isViewMode"
					:class="{ 'custom-disabled': isViewMode }"
				/>
				<b-icon-info-circle class="ml-2 mt-2"
					v-if="isUserSeparatingParty"
					v-b-tooltip.html="`Usually, you will have a mortgage or line of credit
					registered on the property. You can choose 'other' if there are family loans,
					liens, or other debts to pay upon transfer or sale.`">
				</b-icon-info-circle>
			</b-form-group>

			<conditional-group
				class="my-3"
				:condition="liability.registered === 'other'"
			>
				<input-group
					v-model="liability.type"
					class="my-3"
					reset-undefined
					input-cols="8"
					label="Type of liability"
					:testid="'additional-property-'+testid+'-liability-type-'+index"
					:disabled="isViewMode"
				/>
			</conditional-group>

			<input-group
				v-model="liability.lastFourDigits"
				class="my-3"
				reset-undefined
				input-cols="4"
				label="Last four digits of the account"
				placeholder="Digits (Optional)"
				:testid="'additional-property-'+testid+'-last-four-digits-'+parsetoString(index)"
				:disabled="isViewMode"
			/>

			<b-form-group
				:id="'input-responsible-'+index +'-'+ propertyIndex"
				:label="dynamicQuestions.responsible"
				:label-for="'responsible-'+index +'-'+ propertyIndex"
			>
				<b-form-select
					:id="'responsible-'+index +'-'+ propertyIndex"
					v-model="liability.responsible"
					size="sm"
					:options="[
						{
							text: isUserSeparatingParty ? 'We are jointly responsible' :  'Parties are jointly responsible',
							value: 'mortgage',
						},
						...partnerChoices,
					]"
					:data-testid="'additional-property-'+testid+'-liability-responsibility-'+parsetoString(index)"
					class="col-lg-8"
					:disabled="isViewMode"
					:class="{ 'custom-disabled': isViewMode }"
				/>
				<b-icon-info-circle class="ml-2"
					v-if="isUserSeparatingParty"
					v-b-tooltip.html="`It's important to identify the person to whom the debt is
					registered, even though under the law it is a family debt.`">
				</b-icon-info-circle>
			</b-form-group>

			<input-group
				v-model="liability.balance"
				reset-undefined
				class="my-3"
				dollar
				label="What is the amount owing?"
				:testid="'additional-property-'+testid+'-amount-owning-'+parsetoString(index)"
				:disabled="isViewMode"
				:toolTipText="`What is the current balance of the debt?`"
			/>
		</list-item>

		<!-- COMMENTING OUT CAPITAL GAINS FOR BETA -->
		<!-- <yes-no-choice
			label="Was the property ever used as the parties’ principal residence?"
			v-model="additionalRealProperty.hasBeenPrincipalResidence"
			resetUndefined
			class="my-3"
			cols="3"
		/>

		<conditional-group
			class="my-3"
			:condition="additionalRealProperty.hasBeenPrincipalResidence"
		>
			<input-group
				class="my-3"
				resetUndefined
				input-cols="4"
				v-model="additionalRealProperty.taxYearsOwned"
				label="How many tax years was the property owned?"
			/>
			<input-group
				label="How many tax years was the property declared as principal residence?"
				v-model="additionalRealProperty.taxYearsPrincipalResidence"
				class="my-3"
				resetUndefined
				input-cols="4"
			/>
		</conditional-group> -->

		<!-- <yes-no-choice
			label="Would you like to factor in future capital gains tax payable to determine the shared equity of the property?"
			v-model="additionalRealProperty.factorInCapitalGains"
			resetUndefined
			class="my-3"
			cols="3"
		/>

		<conditional-group 
			class="my-3"
			:condition="additionalRealProperty.factorInCapitalGains"
		>
			An estimated capital gain tax of ${{ this.captialGainsCalculation }}
			was calculated based on a tax rate of 35 percent.

			<yes-no-choice
				label="Would you like to use this amount?"
				v-model="additionalRealProperty.useCapitalGainsValue"
				resetUndefined
				class="my-3"
				cols="3"
			/> -->

		<!--<conditional-group
				class="my-3"
				:condition="!additionalRealProperty.useCapitalGainsValue"
			>
				 <input-group
					class="my-3"
					resetUndefined
					input-cols="6"
					dollar
					v-model="additionalRealProperty.capitalGainsOverride"
					label="Override amount: "
				
			</conditional-group> /> 
		</conditional-group> -->
		<template v-if="!clientUserAgreement">
		<yes-no-choice
			v-model="additionalRealProperty.hasExclusions"
			label="Are there any exclusions related to the property?"
			reset-undefined
			class="my-3"
			:testid="'additional-property-has-exclusion-'+testid"
			:disabled="isViewMode"
			:toolTipText="`An exclusion under the law is a portion of the equity that should
			be retained by the person rather than shared. Legal exclusions include parts
			that were obtained through inheritance, gift, or property that was owned before
			you and your spouse began living together. The amount claimed should be the
			original amount received or existing when you started to live together. All
			growth in the property will be shared under the law. Learn more in the Knowledge
			Base in this section. There is an option, to agree to exclude a
			portion of the property.`"
		/>

		<conditional-group
			class="my-3"
			:condition="additionalRealProperty.hasExclusions"
		>
			<exclusion-table
				v-model="additionalRealProperty.exclusions"
				:default-amount="additionalRealProperty.currentValue"
				:default-owner="additionalRealProperty.ownership"
				:agreement-data="agreementData"
				:testid="'additional-property-exclusion-'+testid+'-'"
				:disabled="isViewMode"
			/>
		</conditional-group>

		<b-form-group
			:id="'input-propertyStatus-'+ propertyIndex"
			:label="dynamicQuestions.propertyStatus"
			:label-for="'propertyStatus-'+ propertyIndex"
			class="my-1"
		>
			<b-form-select
				:id="'propertyStatus-'+ propertyIndex"
				v-model="additionalRealProperty.propertyStatus"
				size="sm"
				:options="[
					{
						text: isUserSeparatingParty ? 'One of us will keep the residence and buy the other out' : 'One party will keep the property',
						value: 'keep',
					},
					{
						text: isUserSeparatingParty ? 'We will sell the property and split the proceeds' : 'The parties will sell the residence and split the proceeds',
						value: 'sell',
					},
					{
						text: isUserSeparatingParty ? 'We will co-own the property for a period of time' : 'The parties will continue to co-own the residence for a period of time',
						value: 'co-own',
					},
				]"
				:data-testid="'additional-property-property-status-'+testid"
				class="col-lg-8"
				:disabled="isViewMode"
				:class="{ 'custom-disabled': isViewMode }"
			/>
			<b-icon-info-circle class="ml-2"
				v-if="isUserSeparatingParty"
				v-b-tooltip.html="`This should be a practical choice. Does one of you want to
				live in the property or retain ownership? Will you sell it? Co-own for some time
				because you can't afford to immediately buy the other out? There are a few
				different possibilities, and Divii will calculate the overall payouts
				accordingly, so you do not necessarily have to liquidate.`">
			</b-icon-info-circle>
		</b-form-group>

		<!-- Keep -->
		<conditional-group
			class="my-3"
			:condition="additionalRealProperty.propertyStatus === 'keep'"
		>
			<b-form-group
				:id="'input-additionalRealProperty-keep-'+ propertyIndex"
				:label="dynamicQuestions.keep"
				label-for="keep"
			>
				<b-form-select
					:id="'additionalRealProperty-keep-'+ propertyIndex"
					v-model="additionalRealProperty.keep"
					size="sm"
					reset-undefined
					:options="[...partnerChoices]"
					:data-testid="'additional-property-keep-'+testid"
					class="col-lg-8"
					:disabled="isViewMode"
					:class="{ 'custom-disabled': isViewMode }"
				/>
			</b-form-group>
		</conditional-group>

		<!-- Sell -->
		<conditional-group
			class="my-3"
			:condition="additionalRealProperty.propertyStatus === 'sell'"
		>
			<b-form-group
				:id="'input-propertyState-'+ propertyIndex"
				label="What is the current state of the property?"
				:label-for="'propertyState-'+ propertyIndex"
			>
				<b-form-select
					:id="'propertyState-'+ propertyIndex"
					v-model="additionalRealProperty.propertyState"
					size="sm"
					reset-undefined
					:options="[
						{
							text: 'To be listed for sale',
							value: 'to-be-listed',
						},
						{
							text: 'Listed for sale',
							value: 'listed',
						},
						{
							text: 'Contract for purchase and sale has been executed',
							value: 'contract-executed',
						},
					]"
					:data-testid="'additional-property-state-'+testid"
					class="col-lg-8"
					:disabled="isViewMode"
					:class="{ 'custom-disabled': isViewMode }"
				/>
			</b-form-group>

			<conditional-group
				class="my-3"
				:condition="
					additionalRealProperty.propertyState === 'to-be-listed'
				"
			>
				<yes-no-choice
					v-model="additionalRealProperty.upgradesBeforeSale"
					:label="dynamicQuestions.upgradesBeforeSale"
					reset-undefined
					class="my-3"
					:testid="'additional-property-upgrades-before-sale-'+testid"
					:disabled="isViewMode"
					:toolTipText="`If you and your spouse need to make any repairs or upgrades to
					the property before it sells, you may specify how you'll share the cost of those
					expenses.`"
				/>

				<conditional-group
					class="my-3"
					:condition="additionalRealProperty.upgradesBeforeSale === true"
				>
					<b-form-group
						:id="'input-upgradesBeforeSale-'+ propertyIndex"
						:label="dynamicQuestions.upgradesBeforeSale2"
						:label-for="'upgradesBeforeSale-'+ propertyIndex"
					>
						<b-form-select
							:id="'upgradesBeforeSale-'+ propertyIndex"
							v-model="additionalRealProperty.upgradesCostDivision"
							size="sm"
							:options="[
								{
									text: isUserSeparatingParty ? 'We will share costs equally' : 'They will share costs equally',
									value: 'equally',
								},
								{
									text: isUserSeparatingParty ? 'We will share costs as agreed' : 'They will share costs as agreed',
									value: 'as-agreed',
								},
							]"
							:data-testid="'additional-property-cost-of-upgrades-'+testid"
							class="col-lg-8"
							:disabled="isViewMode"
							:class="{ 'custom-disabled': isViewMode }"
						/>
						<b-icon-info-circle class="ml-2"
							v-if="isUserSeparatingParty"
							v-b-tooltip.html="`Many people will share these costs equally given the shared ownership of the property.`">
						</b-icon-info-circle>
					</b-form-group>
				</conditional-group>
			</conditional-group>

			<conditional-group
				class="my-3"
				:condition="additionalRealProperty.propertyState === 'listed'"
			>
				<input-group
					v-model="additionalRealProperty.realEstateAgent"
					input-cols="8"
					label="Name of real estate agent"
					class="mb-3"
					:testid="'additional-property-real-estate-agent-'+testid"
					:disabled="isViewMode"
				/>

				<input-group
					v-model="additionalRealProperty.listingPrice"
					label="Listing Price"
					class="mb-3"
					dollar
					:testid="'additional-property-listing-price-'+testid"
					:disabled="isViewMode"
				/>
			</conditional-group>

			<conditional-group
				class="my-3"
				:condition="
					additionalRealProperty.propertyState === 'contract-executed'
				"
			>
				<input-group
					v-model="additionalRealProperty.salePrice"
					label="What was the sale price of the property?"
					class="mb-3"
					dollar
					:testid="'additional-property-sale-price-'+testid"
					:disabled="isViewMode"
				/>

				<date-input
					v-model="additionalRealProperty.saleClosingDate"
					label="What is the closing date of the sale?"
					class="mb-3"
					:testid="'additional-property-sale-closing-date-'+testid"
					:disabled="isViewMode"
				/>
			</conditional-group>

			<yes-no-choice
				v-model="additionalRealProperty.someoneWillLiveUntilSold"
				:label="dynamicQuestions.someoneWillLiveUntilSold"
				reset-undefined
				class="my-3"
				:testid="'additional-property-will-live-until-sold-'+testid"
				:disabled="isViewMode"
			/>

			<conditional-group
				class="my-3"
				:condition="additionalRealProperty.someoneWillLiveUntilSold"
			>
				<b-form-group
					:id="'input-additionalRealProperty-liveInUntilSold-'+ propertyIndex"
					:label="dynamicQuestions.liveInUntilSold"
					label-for="liveInUntilSold"
				>
					<b-form-select
						:id="'additionalRealProperty-liveInUntilSold-'+ propertyIndex"
						v-model="additionalRealProperty.liveInUntilSold"
						size="sm"
						reset-undefined
						:options="[...partnerChoices]"
						:data-testid="'additional-property-live-in-until-sold-'+testid"
						class="col-lg-8"
						:disabled="isViewMode"
						:class="{ 'custom-disabled': isViewMode }"
					/>
				</b-form-group>
			</conditional-group>

			<conditional-group
				class="my-3"
				:condition="!additionalRealProperty.someoneWillLiveUntilSold"
			>
				<b-form-group
					:id="'input-useProperty-'+ propertyIndex"
					:label="dynamicQuestions.useProperty"
					:label-for="'useProperty-'+ propertyIndex"
				>
					<b-form-select
						:id="'useProperty-'+ propertyIndex"
						v-model="additionalRealProperty.useProperty"
						size="sm"
						reset-undefined
						:options="[
							...partnerChoices,
							{ text: 'Both', value: 'both' },
							{ text: 'Neither', value: 'neither' },
						]"
						:data-testid="'additional-property-usage-'+testid"
						class="col-lg-8"
						:disabled="isViewMode"
						:class="{ 'custom-disabled': isViewMode }"
					/>
					<b-icon-info-circle class="ml-2"
						v-if="isUserSeparatingParty"
						v-b-tooltip.html="`While you wait for the sale to close, either or both of you
						may have use of the property?`">
					</b-icon-info-circle>
				</b-form-group>
			</conditional-group>

			<b-form-group
				:id="'input-manageProperty-'+ propertyIndex"
				:label="dynamicQuestions.manageProperty"
				:label-for="'manageProperty-'+ propertyIndex"
			>
				<b-form-select
					:id="'manageProperty-'+ propertyIndex"
					v-model="additionalRealProperty.manageProperty"
					size="sm"
					reset-undefined
					:options="[
						...partnerResponsibleChoices,
						...manageOptions
					]"
					:data-testid="'additional-property-management-expenses-'+testid"
					class="col-lg-8"
					:disabled="isViewMode"
					:class="{ 'custom-disabled': isViewMode }"
				/>
				<b-icon-info-circle class="ml-2"
					v-if="isUserSeparatingParty"
					v-b-tooltip.html="`Sometimes it's necessary to share expenses until the property
					is sold. In this case, you want to be mindful of how you'll share costs. You
					have a few general options on how to handle sharing expenses, or you can use our
					Expense Schedule to define the details. `">
				</b-icon-info-circle>
			</b-form-group>

			<yes-no-choice
				v-model="additionalRealProperty.currentlyRented"
				:label="dynamicQuestions.currentlyRented"
				reset-undefined
				class="my-3"
				:testid="'additional-property-currently-rented-income-'+testid"
				:disabled="isViewMode"
			/>

			<conditional-group
				class="my-3"
				:condition="additionalRealProperty.currentlyRented"
			>
				<input-group
					v-model="additionalRealProperty.rentalAmount"
					reset-undefined
					class="my-3"
					dollar
					label="What is the rental income amount?"
					:testid="'additional-property-rental-amount-'+testid"
					:disabled="isViewMode"
				/>

				<b-form-group
					:id="'input-manageRentalIncome-'+ propertyIndex"
					:label="dynamicQuestions.manageRentalIncome"
					:label-for="'manageRentalIncome-'+ propertyIndex"
				>
					<b-form-select
						:id="'manageRentalIncome-'+ propertyIndex"
						v-model="additionalRealProperty.manageRentalIncome"
						size="sm"
						reset-undefined
						:options="manageRentalIncomeOptions"
						:data-testid="'additional-property-rental-income-management-'+testid"
						class="col-lg-8"
						:disabled="isViewMode"
						:class="{ 'custom-disabled': isViewMode }"
					/>
					<b-icon-info-circle class="ml-2"
						v-if="isUserSeparatingParty"
						v-b-tooltip.html="`As landlords you are both equally entitled to the rental
						income generated, unless otherwise agreed. Often people will balance the rental
						income against other expenses. You can explore this option by using the Expense
						Schedule, just make sure to reference it in the Editor.`">
					</b-icon-info-circle>
				</b-form-group>
			</conditional-group>
		</conditional-group>

		<!-- co-own -->
		<conditional-group
			class="my-3"
			:condition="additionalRealProperty.propertyStatus === 'co-own'"
		>
			<b-form-group
				:id="'input-coOwnDecision-'+ propertyIndex"
				:label="dynamicQuestions.coOwnDecision"
				:label-for="'coOwnDecision-'+ propertyIndex"
			>
				<b-form-select
					:id="'coOwnDecision-'+ propertyIndex"
					v-model="additionalRealProperty.coOwnDecision"
					size="sm"
					:options="[
						{ text: 'A specific date', value: 'specific-date' },
						{
							text: isUserSeparatingParty ? 'On a date as agreed upon between us' :  'On a date as agreed upon between the parties',
							value: 'arranged-date',
						},
					]"
					:data-testid="'additional-property-co-own-decision-'+testid"
					class="col-lg-8"
					:disabled="isViewMode"
					:class="{ 'custom-disabled': isViewMode }"
				/>
				<b-icon-info-circle class="ml-2"
					v-if="isUserSeparatingParty"
					v-b-tooltip.html="`When you co-own the property, you both will have equity tied
					up. You each need to think about when and how this arrangement will end, so that
					you can each access these funds for your own purposes. By selecting a date now,
					you are specifying when you will end the co-ownership period. However you may
					agree on a date agreed upon by you in the future. You may also change the
					wording in the Editor to allow for a date determined by either party, which
					allows either party to notify of the intention to trigger a transfer or sale.`">
				</b-icon-info-circle>
			</b-form-group>

			<conditional-group
				class="my-3"
				:condition="
					additionalRealProperty.coOwnDecision === 'specific-date'
				"
			>
				<date-input
					v-model="additionalRealProperty.coOwnDecisionDate"
					label="Specified Date"
					class="mb-3"
					:testid="'additional-property-co-own-decision-date-'+testid"
					:disabled="isViewMode"
				/>
			</conditional-group>

			<yes-no-choice
				v-model="additionalRealProperty.purchaseAdditionalProperty"
				:label="dynamicQuestions.purchaseAdditionalProperty"
				reset-undefined
				class="my-3"
				:testid="'additional-property-purchase-other-party-property-'+testid"
				:disabled="isViewMode"
				:toolTipText="`You may give one person the first right to make an offer to buy
				the other's interest in the property - particularly if one person will be
				residing in the property.`"
			/>

			<conditional-group
				class="my-3"
				:condition="additionalRealProperty.purchaseAdditionalProperty"
			>
				<b-form-group
					:id="'input-whoCanPurchase-'+ propertyIndex"
					:label="dynamicQuestions.whoCanPurchase"
					:label-for="'whoCanPurchase-'+ propertyIndex"
				>
					<b-form-select
						:id="'whoCanPurchase-'+ propertyIndex"
						v-model="additionalRealProperty.whoCanPurchase"
						size="sm"
						:options="[...partnerChoices]"
						:data-testid="'additional-property-who-can-purchase-'+testid"
						class="col-lg-8"
						:disabled="isViewMode"
						:class="{ 'custom-disabled': isViewMode }"
					/>
				</b-form-group>
			</conditional-group>

			<yes-no-choice
				v-model="additionalRealProperty.someoneWillLiveUntilSold"
				:label="dynamicQuestions.someoneWillLiveUntilSold"
				reset-undefined
				class="my-3"
				:testid="'additional-property-either-party-live-until-sold-'+testid"
				:disabled="isViewMode"
			/>

			<conditional-group
				class="my-3"
				:condition="additionalRealProperty.someoneWillLiveUntilSold"
			>
				<radio-group
					:id="'input-additionalRealProperty-liveInUntilSold-'+ propertyIndex"
					v-model="additionalRealProperty.liveUntilSold"
					class="mb-3"
					input-cols="6"
					:label="dynamicQuestions.liveInUntilSold"
					reset-undefined
					select
					:options="[...partnerChoices]"
					:testid="'additional-property-who-live-'+testid"
					:disabled="isViewMode"
				/>

				<conditional-group
					class="my-3"
					:condition="additionalRealProperty.liveUntilSold"
				>
					<yes-no-choice
						:id="'input-additionalRealProperty-personLivingInResidenceResponsibleForExpenses-'+ propertyIndex"
						v-model="
							additionalRealProperty.personLivingInResidenceResponsibleForExpenses
						"
						:label="dynamicQuestions.personLivingInResidenceResponsibleForExpenses"
						reset-undefined
						class="my-3"
						:testid="'additional-property-solely-responsible-for-expenses-'+testid"
						:disabled="isViewMode"
					/>

					<conditional-group
						class="my-3"
						:condition="
							!additionalRealProperty.personLivingInResidenceResponsibleForExpenses
						"
					>
						<radio-group
							:id="'input-additionalRealProperty-manageExpenses-'+ propertyIndex"
							v-model="additionalRealProperty.manageExpenses"
							select
							:label="dynamicQuestions.manageExpenses"
							:options="[
								...manageOptions
							]"
							:testid="'additional-property-property-management-until-sold-'+testid"
							:length="'long'"
							input-cols="8"
							:disabled="isViewMode"
							:toolTipText="`Sometimes it's necessary to share expenses until the property is
							transferred or sold. In this case, you want to be mindful of how you'll share
							costs. You have a few general options on how to handle sharing expenses, or you
							can use our Expense Schedule to define the details.`"
						/>
					</conditional-group>
				</conditional-group>
			</conditional-group>

			<conditional-group
				class="my-3"
				:condition="!additionalRealProperty.someoneWillLiveUntilSold"
			>
				<b-form-group
					:id="'input-useProperty-'+ propertyIndex"
					:label="dynamicQuestions.useProperty"
					:label-for="'useProperty-'+ propertyIndex"
				>
					<b-form-select
						:id="'useProperty-'+ propertyIndex"
						v-model="additionalRealProperty.useProperty"
						size="sm"
						reset-undefined
						:options="[
							...partnerChoices,
							{ text: 'Both', value: 'both' },
							{ text: 'Neither', value: 'neither' },
						]"
						:data-testid="'additional-property-will-either-party-have-use-'+testid"
						class="col-lg-8"
						:disabled="isViewMode"
						:class="{ 'custom-disabled': isViewMode }"
					/>
				</b-form-group>

				<b-form-group
					:id="'input-manageExpenses-'+ propertyIndex"
					:label="dynamicQuestions.manageProperty"
					:label-for="'manageExpenses-'+ propertyIndex"
				>
					<b-form-select
						:id="'manageExpenses-'+ propertyIndex"
						v-model="additionalRealProperty.manageExpenses"
						size="sm"
						:options="[
							...partnerResponsibleChoices,
							...manageOptions
						]"
						:data-testid="'additional-property-expense-management-'+testid"
						class="col-lg-8"
						:disabled="isViewMode"
						:class="{ 'custom-disabled': isViewMode }"
					/>
				</b-form-group>
			</conditional-group>

			<yes-no-choice
				v-model="additionalRealProperty.currentlyRented"
				:label="dynamicQuestions.currentlyRented"
				reset-undefined
				class="my-3"
				:testid="'additional-property-currently-rented-'+testid"
				:disabled="isViewMode"
			/>

			<conditional-group
				class="my-3"
				:condition="additionalRealProperty.currentlyRented"
			>
				<input-group
					v-model="additionalRealProperty.rentalAmount"
					reset-undefined
					class="my-3"
					dollar
					label="What is the rental income amount?"
					:testid="'additional-property-rental-income-amount-'+testid"
					:disabled="isViewMode"
				/>

				<b-form-group
					:id="'input-manageRentalIncome-'+ propertyIndex"
					:label="dynamicQuestions.manageRentalIncome"
					:label-for="'manageRentalIncome-'+ propertyIndex"
				>
					<b-form-select
						:id="'manageRentalIncome-'+ propertyIndex"
						v-model="additionalRealProperty.manageRentalIncome"
						size="sm"
						reset-undefined
						:options="manageRentalIncomeOptions"
						:data-testid="'additional-property-manage-rental-income-'+testid"
						class="col-lg-8"
						:disabled="isViewMode"
						:class="{ 'custom-disabled': isViewMode }"
					/>
					<b-icon-info-circle class="ml-2"
						v-if="isUserSeparatingParty"
						v-b-tooltip.html="`As landlords you are both equally entitled to the rental
						income generated, unless otherwise agreed. Often people will balance the rental
						income against other expenses. You can explore this option by using the Expense
						Schedule, just make sure to reference it in the Editor.`"/>
				</b-form-group>
			</conditional-group>
		</conditional-group>
		</template>
		<template v-if="!clientUserAgreement">
			<additional-clauses
				:newLabel="'Add Clause to Recitals'" class="mt-2"
				v-model="additionalRealProperty.additionalClausesToRecitals" :title="'Additional Clauses into Recitals'" :testid="'additional-property-add-clause-to-recitals-'+testid+'-'"
				:disabled="isViewMode"
			/>

			<additional-clauses
				:newLabel="'Add Clause to Section'" class="mt-2"
				v-model="additionalRealProperty.additionalClauses" :title="'Additional Clauses into Sections'" :testid="'additional-property-add-clause-'+testid+'-'"
				:disabled="isViewMode"	
			/>
		</template>
	</div>
</template>
